import { FREE_TRIAL_MONTHS } from '../../config/onboarding';

/** Plateforme Production (espace entreprise des exploitations). */
export const PRODUCTION_PLATFORM_URL = 'https://entreprise.assiganto-agro.com';

/**
 * Questions récurrentes du chatbot. Les textes (question, réponse, liens) sont dans i18n
 * (`chatbot.faq.<id>`) ; ici, les mots-clés de reconnaissance (sans accents, en minuscules)
 * et les liens d'action vers les pages du landing (`to`) ou vers une autre application (`href`).
 */
export const FAQ = [
    {
        id: 'create_company',
        keywords: {
            FR: ['compte entreprise', 'creer entreprise', 'inscrire entreprise', 'inscription entreprise', 'cooperative', 'societe', 'entreprise'],
            EN: ['business account', 'company account', 'register company', 'company', 'business', 'cooperative'],
        },
        actions: [{ to: '/signup', label: 'signup' }],
    },
    {
        id: 'create_individual',
        keywords: {
            FR: ['compte particulier', 'creer particulier', 'inscrire particulier', 'particulier', 'individuel', 'travailleur', 'postuler'],
            EN: ['individual account', 'personal account', 'individual', 'personal', 'worker', 'apply'],
        },
        actions: [{ to: '/signup', label: 'signup' }],
    },
    {
        id: 'cost',
        keywords: {
            FR: ['combien', 'cout', 'coute', 'prix', 'tarif', 'payer', 'gratuit', 'essai', 'commission', 'frais'],
            EN: ['how much', 'cost', 'price', 'pricing', 'pay', 'free', 'trial', 'commission', 'fee'],
        },
        actions: [{ to: '/pricing', label: 'pricing' }],
    },
    {
        id: 'cancel',
        keywords: {
            FR: ['resilier', 'resiliation', 'annuler', 'arreter', 'desactiver', 'desabonner', 'supprimer mon compte', 'engagement'],
            EN: ['cancel', 'cancellation', 'unsubscribe', 'stop', 'deactivate', 'delete my account', 'commitment'],
        },
        actions: [{ to: '/cancellation-policy', label: 'cancellation_policy' }],
    },
    {
        id: 'contact',
        keywords: {
            FR: ['contacter', 'contact', 'difficulte', 'probleme', 'aide', 'support', 'assistance', 'joindre', 'telephone', 'email'],
            EN: ['contact', 'difficulty', 'problem', 'help', 'support', 'assistance', 'reach', 'phone', 'email'],
        },
        actions: [{ to: '/contact', label: 'contact_page' }],
        contact: true,
    },
    {
        id: 'documents',
        keywords: {
            FR: ['document', 'documents', 'piece', 'identite', 'registre', 'rccm', 'fiscal', 'justificatif'],
            EN: ['document', 'documents', 'identity', 'id card', 'registration certificate', 'tax', 'proof'],
        },
        actions: [],
    },
    {
        id: 'company_or_individual',
        keywords: {
            FR: ['difference', 'choisir', 'quel compte', 'lequel'],
            EN: ['difference', 'choose', 'which account', 'which one'],
        },
        actions: [{ to: '/signup', label: 'signup' }],
    },
    {
        id: 'language',
        keywords: {
            FR: ['langue', 'anglais', 'francais', 'traduire', 'traduction'],
            EN: ['language', 'english', 'french', 'translate', 'translation'],
        },
        actions: [],
    },
    {
        id: 'password',
        keywords: {
            FR: ['mot de passe', 'oublie', 'connexion', 'connecter', 'identifiant'],
            EN: ['password', 'forgot', 'login', 'log in', 'sign in', 'credentials'],
        },
        actions: [{ to: '/forgot-password', label: 'forgot_password' }],
    },
    {
        id: 'production_access',
        keywords: {
            FR: ['plateforme production', 'production', 'espace entreprise', 'tableau de bord'],
            EN: ['production platform', 'production', 'business space', 'dashboard'],
        },
        // Autre application que le landing : lien externe, pas une route.
        actions: [{ href: PRODUCTION_PLATFORM_URL, label: 'production' }],
    },
];

/** Questions proposées à l'ouverture, dans cet ordre. */
export const SUGGESTED = ['create_company', 'create_individual', 'production_access', 'cost', 'cancel', 'contact'];

/** Minuscules, sans accents ni ponctuation : « Coûte-t-il ? » → « coute t il ». */
export const normalize = (text) =>
    (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9@]+/g, ' ')
        .trim();

/**
 * Entrées de la FAQ qui répondent à la question, de la plus proche à la moins proche. Un
 * mot-clé de plusieurs mots pèse plus qu'un mot isolé : « compte particulier » l'emporte sur
 * « compte entreprise » si la question parle de particulier. Les mots-clés des deux langues
 * sont reconnus, ceux de la langue affichée comptent double.
 */
export const matchFaqs = (question, language = 'FR', limit = FAQ.length) => {
    const text = ` ${normalize(question)} `;
    if (!text.trim()) return [];
    return FAQ.map((entry) => {
        const own = entry.keywords[language] || [];
        const other = Object.entries(entry.keywords).filter(([lang]) => lang !== language).flatMap(([, words]) => words);
        const score = [...own, ...other].reduce((sum, keyword) => {
            const words = normalize(keyword);
            return text.includes(` ${words} `) ? sum + words.split(' ').length * (own.includes(keyword) ? 2 : 1) : sum;
        }, 0);
        return { entry, score };
    })
        .filter(({ score }) => score > 0)
        // Tri stable : à score égal, l'ordre de la FAQ est conservé.
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(({ entry }) => entry);
};

/** Entrée de la FAQ la plus proche de la question, ou `null`. */
export const matchFaq = (question, language = 'FR') => matchFaqs(question, language, 1)[0] || null;

/** Remplace {email}, {months}… par leur valeur. */
export const fill = (text, values) => (text || '').replace(/\{(\w+)\}/g, (all, name) => (name in values ? values[name] : all));

/** Paragraphes de la réponse ; la mention de l'essai gratuit disparaît si FREE_TRIAL_MONTHS vaut 0. */
export const answerOf = (entry, texts, values) => {
    const faq = texts.faq[entry.id];
    const paragraphs = [...faq.answer];
    if (entry.id === 'cost' && FREE_TRIAL_MONTHS > 0 && faq.trial) paragraphs.splice(1, 0, faq.trial);
    return paragraphs.map((paragraph) => fill(paragraph, values));
};
